import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Box, Typography, Stack, IconButton, CircularProgress, AspectRatio } from '@mui/joy';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import { useGetEventsQuery, useDeleteEventMutation } from '@/features/events/api';
import { DeleteAction } from '@/components/DeleteAction';
import { useCrudHouseholdMutation } from '@/hooks/useCrudHouseholdMutation';
import { AddEventForm } from '../components/AddEventForm';
import { EditingEventRow } from '../components/EditingEventRow';
import { EventRowShell } from '../components/EventRowShell';

export function SettingsEventsPage() {
  const { householdId } = useParams();
  const { data, isLoading } = useGetEventsQuery({ householdId });

  const events = data?.data || [];

  return (
    <Card variant="outlined" sx={{ p: 2.5 }}>
      <Box sx={{ mb: 2 }}>
        <Typography level="h4">Events</Typography>
        <Typography level="body-sm">
          Create visual placeholders for meal-plan slots that aren&apos;t a recipe
        </Typography>
      </Box>
      <Stack gap={3.5}>
        <AddEventForm householdId={householdId} />
        {isLoading ? <CircularProgress /> : <EventsList events={events} />}
      </Stack>
    </Card>
  );
}

function EventsList({ events }) {
  const [editingEventId, setEditingEventId] = useState(null);

  if (events.length === 0) {
    return (
      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
        No events yet. Create one to get started!
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {events.map((event) => (
        <EventRow
          key={event.id}
          event={event}
          isEditing={editingEventId === event.id}
          onStartEdit={() => setEditingEventId(event.id)}
          onStopEdit={() => setEditingEventId(null)}
        />
      ))}
    </Box>
  );
}

function EventRow({ event, isEditing, onStartEdit, onStopEdit }) {
  const { householdId } = useParams();
  const useDelete = useCrudHouseholdMutation(householdId, useDeleteEventMutation);

  if (isEditing) {
    return <EditingEventRow event={event} householdId={householdId} onStopEdit={onStopEdit} />;
  }

  return (
    <EventRowShell>
      <AspectRatio ratio="1" sx={{ width: 36, borderRadius: 'sm', flexShrink: 0, ml: '-2px' }}>
        {event.image?.url ? (
          <img src={event.image.url} srcSet={event.image.srcset} sizes="36px" alt={event.name} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.tertiary' }}>
            <EventIcon fontSize="small" />
          </Box>
        )}
      </AspectRatio>
      <Typography level="body-md" sx={{ flex: 1, ml: 0.5 }}>
        {event.name}
      </Typography>
      <IconButton size="sm" variant="plain" color="neutral" onClick={onStartEdit}>
        <EditIcon fontSize="small" />
      </IconButton>
      <DeleteAction id={event.id} name={event.name} useDelete={useDelete} />
    </EventRowShell>
  );
}
